import api from './client';

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.put('/auth/change-password', { oldPassword, newPassword }),
};

export const usersApi = {
  list: (params?: Record<string, any>) => api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  setPassword: (id: string, password: string) => api.put(`/users/${id}/password`, { password }),
  remove: (id: string) => api.delete(`/users/${id}`),
};

export const companiesApi = {
  list: (params?: Record<string, any>) => api.get('/companies', { params }),
  getById: (id: string) => api.get(`/companies/${id}`),
  create: (data: any) => api.post('/companies', data),
  update: (id: string, data: any) => api.put(`/companies/${id}`, data),
  remove: (id: string) => api.delete(`/companies/${id}`),
};

export const contactsApi = {
  list: (params?: Record<string, any>) => api.get('/contacts', { params }),
  getById: (id: string) => api.get(`/contacts/${id}`),
  create: (data: any) => api.post('/contacts', data),
  update: (id: string, data: any) => api.put(`/contacts/${id}`, data),
  remove: (id: string) => api.delete(`/contacts/${id}`),
};

export const leadsApi = {
  list: (params?: Record<string, any>) => api.get('/leads', { params }),
  getById: (id: string) => api.get(`/leads/${id}`),
  create: (data: any) => api.post('/leads', data),
  update: (id: string, data: any) => api.put(`/leads/${id}`, data),
  convert: (id: string) => api.post(`/leads/${id}/convert`),
  remove: (id: string) => api.delete(`/leads/${id}`),
};

export const dealsApi = {
  list: (params?: Record<string, any>) => api.get('/deals', { params }),
  getKanban: (params?: Record<string, any>) => api.get('/deals/kanban', { params }),
  getById: (id: string) => api.get(`/deals/${id}`),
  create: (data: any) => api.post('/deals', data),
  update: (id: string, data: any) => api.put(`/deals/${id}`, data),
  changeStage: (id: string, stage: string, lossReason?: string) =>
    api.put(`/deals/${id}/stage`, { stage, lossReason }),
  remove: (id: string) => api.delete(`/deals/${id}`),
};

export const tasksApi = {
  list: (params?: Record<string, any>) => api.get('/tasks', { params }),
  getById: (id: string) => api.get(`/tasks/${id}`),
  create: (data: any) => api.post('/tasks', data),
  update: (id: string, data: any) => api.put(`/tasks/${id}`, data),
  remove: (id: string) => api.delete(`/tasks/${id}`),
};

export const activitiesApi = {
  list: (params?: Record<string, any>) => api.get('/activities', { params }),
  getTimeline: (entityType: string, entityId: string) =>
    api.get(`/activities/timeline/${entityType}/${entityId}`),
  create: (data: any) => api.post('/activities', data),
};

export const commentsApi = {
  list: (params?: Record<string, any>) => api.get('/comments', { params }),
  create: (data: any) => api.post('/comments', data),
  remove: (id: string) => api.delete(`/comments/${id}`),
};

export const notificationsApi = {
  list: (params?: Record<string, any>) => api.get('/notifications', { params }),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  clearAll: () => api.delete('/notifications'),
};

export const reportsApi = {
  dashboard: () => api.get('/reports/dashboard'),
  leadsBySource: () => api.get('/reports/leads-by-source'),
  dealsByManager: () => api.get('/reports/deals-by-manager'),
};

export const importExportApi = {
  importCompanies: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/companies', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importContacts: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/contacts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importLeads: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/leads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importDeals: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/deals', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importTasks: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/import/tasks', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportCompanies: () => api.get('/export/companies', { responseType: 'blob' }),
  exportContacts: () => api.get('/export/contacts', { responseType: 'blob' }),
  exportLeads: () => api.get('/export/leads', { responseType: 'blob' }),
  exportDeals: () => api.get('/export/deals', { responseType: 'blob' }),
  exportTasks: () => api.get('/export/tasks', { responseType: 'blob' }),
};

export const aiApi = {
  chat: (data: { pageType: string; pagePath?: string; entityType?: string; entityId?: string; userMessage?: string }) => {
    const token = localStorage.getItem('svcrm_token');
    return fetch('/api/ai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
  },
  getLastResponse: (params: { pageType: string; entityType?: string; entityId?: string }) =>
    api.get('/ai/last-response', { params }),
};

export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: (data: { name: string; permissions?: any; expiresAt?: string }) =>
    api.post('/api-keys', data),
  update: (id: string, data: { name?: string; isActive?: boolean; permissions?: any; expiresAt?: string }) =>
    api.put(`/api-keys/${id}`, data),
  revoke: (id: string) => api.put(`/api-keys/${id}/revoke`),
  remove: (id: string) => api.delete(`/api-keys/${id}`),
  stats: (id: string, days?: number) =>
    api.get(`/api-keys/${id}/stats`, { params: { days } }),
  globalStats: (days?: number) =>
    api.get('/api-keys/stats/global', { params: { days } }),
};

export const apiDocsApi = {
  getSpec: () => fetch('/api/v1/docs').then(r => r.json()),
};
